import React from 'react';
import {
  BathFixtureShell,
  Rect,
  Line,
  Circle,
  Curve,
} from './BathFixtureFrame';
import {
  faucetPrimitives,
  barEnd,
} from '../../3d/geometry/proceduralRenderers/faucetParts';
import {
  offsetOutline,
  polygonToSvgPath,
  samplePath,
  svgNum,
} from '../../3d/geometry/proceduralRenderers/faucetPath';

/**
 * Orthographic elevation of ANY faucet profile, drawn from the same
 * `faucetPrimitives` list the 3D renderer builds its meshes from.
 *
 * Front view: viewBox = width x height, horizontal = x.
 * Side views: viewBox = depth x height, horizontal = u (the reach); the LEFT
 * view is drawn and the RIGHT view mirrors it, exactly as the other bath
 * schematics do.
 *
 * Every part projects by one rule per kind:
 *   box  -> its rectangle in that plane
 *   cyl  -> a rectangle when its axis lies in the view plane, a circle when
 *           it points at the viewer
 *   bar  -> a rounded bar between its projected end points (a lever in the
 *           other plane foreshortens to a vertical stub)
 *   tube -> the exact offset outline in the side view; end-on in the front
 *           view it is the column of the tube's width over its vertical extent
 * Drafting convention: outlines only, no fill, thin uniform strokes.
 */

const CAP_SAMPLES = 6;

export function FaucetElevation({
  profile,
  view = 'front',
  widthMeters,
  heightMeters,
  depthMeters,
  strokeColor,
  strokeWidth,
}) {
  const side = view === 'left' || view === 'right';
  const horiz = side ? depthMeters : widthMeters;
  const proj = {
    side,
    h: (x, u) => horiz / 2 + (side ? u : x),
    y: (v) => heightMeters - v,
  };
  const stroke = { strokeColor, strokeWidth };
  const parts = faucetPrimitives(profile);
  return (
    <BathFixtureShell width={horiz} height={heightMeters}>
      <g
        data-faucet-variant={profile.variant || profile.mount}
        transform={
          view === 'right' ? `translate(${horiz}, 0) scale(-1, 1)` : undefined
        }
      >
        <DeckOrWallLine
          profile={profile}
          proj={proj}
          horiz={horiz}
          height={heightMeters}
          {...stroke}
        />
        {parts.map((p) => (
          <Part key={p.name} p={p} proj={proj} {...stroke} />
        ))}
      </g>
    </BathFixtureShell>
  );
}

/** Ground line: the deck under a deck faucet, the wall behind a wall one. */
function DeckOrWallLine({
  profile,
  proj,
  horiz,
  height,
  strokeColor,
  strokeWidth,
}) {
  if (profile.mount === 'wall') {
    if (!proj.side) return null;
    return (
      <Line
        x1={0}
        y1={0}
        x2={0}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return (
    <Line
      x1={0}
      y1={height}
      x2={horiz}
      y2={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

function Part({ p, proj, strokeColor, strokeWidth }) {
  const stroke = { strokeColor, strokeWidth };
  switch (p.kind) {
    case 'box':
      return <BoxPart p={p} proj={proj} {...stroke} />;
    case 'cyl':
      return <CylPart p={p} proj={proj} {...stroke} />;
    case 'bar':
      return <BarPart p={p} proj={proj} {...stroke} />;
    case 'tube':
      return <TubePart p={p} proj={proj} {...stroke} />;
    default:
      return null;
  }
}

function BoxPart({ p, proj, ...stroke }) {
  const [h0, h1] = proj.side ? p.u : p.x;
  const [v0, v1] = p.v;
  return (
    <Rect
      x={proj.h(h0, h0)}
      y={proj.y(v1)}
      width={h1 - h0}
      height={v1 - v0}
      {...stroke}
    />
  );
}

function CylPart({ p, proj, ...stroke }) {
  const [s0, s1] = p.span;
  if (p.axis === 'v') {
    const hc = proj.h(p.at.x, p.at.u);
    if (p.taper && p.taper !== 1) {
      // Tapered: a trapezoid, narrower at the top.
      const rt = p.r * p.taper;
      return (
        <Curve
          d={[
            `M ${svgNum(hc - p.r)} ${svgNum(proj.y(s0))}`,
            `L ${svgNum(hc + p.r)} ${svgNum(proj.y(s0))}`,
            `L ${svgNum(hc + rt)} ${svgNum(proj.y(s1))}`,
            `L ${svgNum(hc - rt)} ${svgNum(proj.y(s1))}`,
            'Z',
          ].join(' ')}
          {...stroke}
        />
      );
    }
    return (
      <Rect
        x={hc - p.r}
        y={proj.y(s1)}
        width={2 * p.r}
        height={s1 - s0}
        {...stroke}
      />
    );
  }
  // Axis lies across the width ('x') or along the reach ('u'): a rectangle
  // in the view that contains it, a circle in the view that looks down it.
  const inPlane = proj.side ? p.axis === 'u' : p.axis === 'x';
  if (inPlane) {
    return (
      <Rect
        x={proj.h(s0, s0)}
        y={proj.y(p.at.v + p.r)}
        width={s1 - s0}
        height={2 * p.r}
        {...stroke}
      />
    );
  }
  const hc = proj.side ? proj.h(0, p.at.u) : proj.h(p.at.x, 0);
  return <Circle cx={hc} cy={proj.y(p.at.v)} r={p.r} {...stroke} />;
}

/**
 * Rounded bar between two projected points, emitted as a polygon so the
 * drawn extents can be read straight off the path (no transforms).
 */
function BarPart({ p, proj, ...stroke }) {
  const end = barEnd(p);
  const a = [proj.h(p.from.x, p.from.u), proj.y(p.from.v)];
  const b = [proj.h(end.x, end.u), proj.y(end.v)];
  return <Curve d={capsulePath(a, b, p.r)} {...stroke} />;
}

function capsulePath(a, b, r) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1e-9;
  const ang = Math.atan2(dy, dx);
  const pts = [];
  const cap = (c, a0) => {
    for (let i = 0; i <= CAP_SAMPLES; i += 1) {
      const t = a0 + (Math.PI * i) / CAP_SAMPLES;
      pts.push([c[0] + r * Math.cos(t), c[1] + r * Math.sin(t)]);
    }
  };
  // Tip cap sweeps from -90° to +90° around the bar direction, base cap the
  // other half. A zero-length bar (fully foreshortened) collapses to a disc.
  if (len < 1e-6) {
    cap(a, 0);
    cap(a, Math.PI);
  } else {
    cap(b, ang - Math.PI / 2);
    cap(a, ang + Math.PI / 2);
  }
  return (
    pts
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${svgNum(x)} ${svgNum(y)}`)
      .join(' ') + ' Z'
  );
}

function TubePart({ p, proj, ...stroke }) {
  if (proj.side) {
    const outline = offsetOutline(p.path, p.r);
    return (
      <Curve
        d={polygonToSvgPath(outline, {
          uOffset: proj.h(0, 0),
          vTop: proj.y(0),
        })}
        {...stroke}
      />
    );
  }
  // End-on: the tube's silhouette is a column 2r wide spanning its rise.
  const vs = samplePath(p.path).map(([, v]) => v);
  const vMin = Math.max(Math.min(...vs) - p.r, p.frontFromV ?? -Infinity);
  const vMax = Math.max(...vs) + p.r;
  const hc = proj.h(p.x, 0);
  return (
    <Rect
      x={hc - p.r}
      y={proj.y(vMax)}
      width={2 * p.r}
      height={vMax - vMin}
      {...stroke}
    />
  );
}
